import { useFormikContext } from 'formik';
import { checkEmail, checkUCN, hasValueAndIsSalesForce } from '@fnol/library/src/lib';
import { bodyHarm, constValues, legalPersonType, personType, requestType, translations } from './consts';

export const useValidateIndividual = (
    individualType: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const individualExistingErrors: { [key: string]: string } = {};
    const individualTouched: { [key: string]: boolean } = {};
    let individualIsValid = true;

    const { values } = useFormikContext<any>();

    const legalPersonTypePropertyName = `${personType[individualType]}Type`;

    const name = `${personType[individualType]}IndividualName`;
    const lastname = `${personType[individualType]}IndividualLastName`;
    const ucn = `${personType[individualType]}IndividualUCN`;
    const country = `${personType[individualType]}IndividualCountry`;
    const city = `${personType[individualType]}IndividualCity`;
    const cityInSerbia = `${personType[individualType]}IndividualCityInSerbia`;
    const street = `${personType[individualType]}IndividualStreet`;
    const email = `${personType[individualType]}IndividualEmail`;
    const phone = `${personType[individualType]}IndividualPhone`;
    const submitterRelationId = `${personType[individualType]}IndividualSubmitterRelationId`; // not exists for submitter
    const shouldShowBodyHarmForm = `${personType[individualType]}IndividualShouldShowBodyHarmForm`;
    const reportingImmaterialDamage = `${personType[individualType]}IndividualReportingImmaterialDamage`;
    const personRoleId = `${personType[individualType]}IndividualPersonRoleId`;
    const duplicatedUcn = `${personType[individualType]}DuplicatedUcn`;

    if (
        values[legalPersonTypePropertyName] === legalPersonType.individual ||
        individualType === personType.injured || // only individuals, so there isn't value for legalPersonTypePropertyName
        individualType === personType.driver
    ) {
        individualTouched[name] = true;
        individualTouched[lastname] = true;
        individualTouched[ucn] = true;
        individualTouched[country] = true;
        individualTouched[city] = true;
        individualTouched[cityInSerbia] = true;
        individualTouched[street] = true;
        individualTouched[email] = true;
        individualTouched[phone] = true;
        if (individualType !== personType.initialValue && individualType !== personType.submitter)
            individualTouched[submitterRelationId] = true;
        if (individualType !== personType.initialValue && individualType !== personType.injured)
            individualTouched[reportingImmaterialDamage] = true;
        individualTouched[personRoleId] = true;

        if (!values[name]) {
            individualExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[lastname]) {
            individualExistingErrors[lastname] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[ucn]) {
            individualExistingErrors[ucn] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[country] === constValues.countrySerbiaId ||
            values[country] === constValues.countrySerbiaId.toString(10)
        ) {
            if (values[ucn] && !checkUCN(values[ucn])) {
                individualExistingErrors[ucn] = translations.validationMessages.UCN_INVALID_FORMAT;
                individualIsValid = false;
            } else {
                if (values[ucn] && values[duplicatedUcn]) {
                    individualExistingErrors[ucn] = ' ';
                    individualIsValid = false;
                }
            }
        }

        if (values[country] === '') {
            individualExistingErrors[country] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId &&
            values[city] === ''
        ) {
            individualExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            (values[country] === constValues.countrySerbiaId.toString(10) ||
                values[country] === constValues.countrySerbiaId) &&
            (!values[cityInSerbia] || values[cityInSerbia].id === '')
        ) {
            individualExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[street] === '') {
            individualExistingErrors[street] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[email] && individualType === personType.submitter && !hasValueAndIsSalesForce(values[phone])) {
            individualExistingErrors[email] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[email] && !checkEmail(values[email])) {
            // check only if there is a value
            individualExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            individualIsValid = false;
        }

        if (!values[phone] && individualType === personType.submitter && !hasValueAndIsSalesForce(values[email])) {
            individualExistingErrors[phone] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (individualType !== personType.injured) {
            if (values[shouldShowBodyHarmForm] === bodyHarm.CanBeSubmitted && !values[reportingImmaterialDamage]) {
                individualExistingErrors[reportingImmaterialDamage] = translations.validationMessages.REQUIRED_FIELD;
                individualIsValid = false;
            }
        } else if (values[shouldShowBodyHarmForm] === bodyHarm.AlreadySubmitted) {
            individualExistingErrors[shouldShowBodyHarmForm] =
                translations.validationMessages.CLAIM_ALREADY_EXISTS_FOR_UCN;
            individualIsValid = false;
        }

        if (
            values[reportingImmaterialDamage] === 'true' &&
            values.requestType !== requestType.MaterialDamage &&
            !values[personRoleId] &&
            individualType !== personType.driver
        ) {
            individualExistingErrors[personRoleId] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            individualType !== personType.initialValue &&
            individualType !== personType.submitter &&
            values[submitterRelationId] === ''
        ) {
            individualExistingErrors[submitterRelationId] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }
    }

    return { errors: individualExistingErrors, touched: individualTouched, isValid: individualIsValid };
};

export const useValidateLegalEntity = (
    legalEntityType: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const legalEntityExistingErrors: { [key: string]: string } = {};
    const legalEntityTouched: { [key: string]: boolean } = {};
    let legalEntityIsValid = true;

    const { values } = useFormikContext<any>();

    const legalPersonTypePropertyName = `${personType[legalEntityType]}Type`;

    const name = `${personType[legalEntityType]}LegalEntityName`;
    const id = `${personType[legalEntityType]}LegalEntityID`;
    const leasingID = `${personType[legalEntityType]}LeasingEntityID`;
    const vat = `${personType[legalEntityType]}LegalEntityVAT`;
    const country = `${personType[legalEntityType]}LegalEntityCountry`;
    const city = `${personType[legalEntityType]}LegalEntityCity`;
    const cityInSerbia = `${personType[legalEntityType]}LegalEntityCityInSerbia`;
    const street = `${personType[legalEntityType]}LegalEntityStreet`;
    const email = `${personType[legalEntityType]}LegalEntityEmail`;
    const personRoleId = `${personType[legalEntityType]}LegalEntityPersonRoleId`;
    const contactName = `${personType[legalEntityType]}LegalEntityContactName`;
    const contactLastName = `${personType[legalEntityType]}LegalEntityContactLastName`;
    const contactEmail = `${personType[legalEntityType]}LegalEntityContactEmail`;
    const contactPhone = `${personType[legalEntityType]}LegalEntityContactPhone`;
    const classificationType = `${personType[legalEntityType]}IndustrialClassificationType`;
    const duplicatedId = `${personType[legalEntityType]}DuplicatedId`;

    if (values[legalPersonTypePropertyName] === legalPersonType.legal) {
        legalEntityTouched[name] = true;
        legalEntityTouched[id] = true;
        legalEntityTouched[leasingID] = true;
        legalEntityTouched[vat] = true;
        legalEntityTouched[country] = true;
        legalEntityTouched[city] = true;
        legalEntityTouched[cityInSerbia] = true;
        legalEntityTouched[street] = true;
        legalEntityTouched[classificationType] = true;
        legalEntityTouched[contactName] = true;
        legalEntityTouched[contactLastName] = true;
        legalEntityTouched[contactEmail] = true;
        legalEntityTouched[contactPhone] = true;

        if (legalEntityType !== personType.initialValue && legalEntityType !== personType.submitter)
            legalEntityTouched[personRoleId] = true;

        if (
            values[classificationType] !== 0 &&
            values[classificationType] !== constValues.industrialClassificationTypeLeasing
        ) {
            if (!values[name]) {
                legalEntityExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            }

            if (!values[id]) {
                legalEntityExistingErrors[id] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            } else {
                if (values[duplicatedId]) {
                    legalEntityExistingErrors[id] = ' ';
                    legalEntityIsValid = false;
                }
            }

            if (!!values.legalEntityIdFieldFormatError) {
                legalEntityExistingErrors[id] = translations.validationMessages.LEGAL_ENTITY_NUMBER_INVALID_FORMAT;
                legalEntityIsValid = false;
            }

            if (!values[vat] || values[vat] === '') {
                legalEntityExistingErrors[vat] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            }

            if (!!values.VATFormatError) {
                legalEntityExistingErrors[vat] = translations.validationMessages.VAT_INVALID_FORMAT;
                legalEntityIsValid = false;
            }

            if (values[country] === '') {
                legalEntityExistingErrors[country] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            }

            if (
                values[country] !== constValues.countrySerbiaId.toString(10) &&
                values[country] !== constValues.countrySerbiaId &&
                values[city] === ''
            ) {
                legalEntityExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            }

            if (
                (values[country] === constValues.countrySerbiaId.toString(10) ||
                    values[country] === constValues.countrySerbiaId) &&
                (!values[cityInSerbia] || values[cityInSerbia].id === '')
            ) {
                legalEntityExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            }

            if (values[street] === '') {
                legalEntityExistingErrors[street] = translations.validationMessages.REQUIRED_FIELD;
                legalEntityIsValid = false;
            }
        } else {
            if (values[classificationType] !== 0) {
                if (!values[leasingID]) {
                    legalEntityExistingErrors[leasingID] = translations.validationMessages.REQUIRED_FIELD;
                    legalEntityIsValid = false;
                }
            }
        }
        if (
            !values[personRoleId] &&
            legalEntityType !== personType.initialValue &&
            legalEntityType !== personType.submitter
        ) {
            legalEntityExistingErrors[personRoleId] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!values[classificationType]) {
            legalEntityExistingErrors[classificationType] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }
    }

    return { errors: legalEntityExistingErrors, touched: legalEntityTouched, isValid: legalEntityIsValid };
};
